import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  MapPin,
  Star,
  Users,
  Clock,
  Car,
  ArrowRight,
  Snowflake,
  Volume2,
  Music,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

interface RoutePreview {
  slug: string;
  from: string;
  to: string;
  departure_time: string | null;
  seats_available: number;
  total_seats: number;
  fare_estimate: number | null;
  distance_km: number;
  is_recurring: boolean;
  recurrence_days: string[];
  preferences: {
    ac?: boolean;
    quietRide?: boolean;
    music?: boolean;
    genderPreference?: string;
  };
  driver: {
    name: string;
    rating: number | null;
    photo: string | null;
    car: string;
    car_color: string | null;
    is_premium: boolean;
  };
}

const DAY_LABELS: Record<string, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function formatDeparture(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GH", {
      weekday: "short",
      day: "numeric",
      month: "short",
    }) +
    " · " +
    d.toLocaleTimeString("en-GH", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
}

function shortenAddress(addr: string): string {
  return addr.split(",")[0].trim();
}

export default function JoinPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [route, setRoute] = useState<RoutePreview | null>(null);
  const [status, setStatus] = useState<
    "loading" | "ok" | "not-found" | "departed" | "error"
  >("loading");

  useEffect(() => {
    if (!slug) {
      setStatus("not-found");
      return;
    }

    fetch(`${API_BASE}/public/route/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        const data = await res.json();
        if (res.status === 410) {
          setStatus("departed");
        } else if (!res.ok || !data.success) {
          setStatus("not-found");
        } else {
          setRoute(data.route);
          setStatus("ok");
        }
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#0057FF] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500 font-medium">
            Loading trip details…
          </p>
        </div>
      </div>
    );
  }

  if (status === "departed") {
    return (
      <ErrorState
        title="Trip already departed"
        body="This ride has already left. Open Yenko to find another driver on your route."
      />
    );
  }

  if (status === "not-found" || !route) {
    return (
      <ErrorState
        title="Link not found"
        body="This trip link is invalid or has been removed. Ask your driver or friend to reshare."
      />
    );
  }

  if (status === "error") {
    return (
      <ErrorState
        title="Something went wrong"
        body="We couldn't load this trip. Check your connection and try again."
      />
    );
  }

  const from = shortenAddress(route.from);
  const to = shortenAddress(route.to);
  const seatsLeft = route.seats_available;
  const isFull = seatsLeft === 0;

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col">
      {/* Top bar */}
      <div className="bg-[#0057FF] px-5 py-4 flex items-center justify-between">
        <span className="text-white font-bold text-xl tracking-tight">
          Yenko
        </span>
        <span className="text-white/70 text-xs font-medium">
          Commute smarter together
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-4 py-8 gap-5 max-w-md mx-auto w-full">
        {/* Hero message */}
        <div className="text-center">
          <p className="text-gray-500 text-sm font-medium mb-1">
            {route.driver.name} invited you to join their commute
          </p>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            {from} → {to}
          </h1>
        </div>

        {/* Route card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 w-full overflow-hidden">
          {/* Driver strip */}
          <div className="bg-gradient-to-br from-[#0057FF] to-[#0041cc] p-5 flex items-center gap-4">
            {route.driver.photo ? (
              <img
                src={route.driver.photo}
                alt={route.driver.name}
                className="w-14 h-14 rounded-2xl object-cover border-2 border-white/30"
              />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold text-xl">
                  {route.driver.name[0].toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-lg leading-tight">
                {route.driver.name}
              </p>
              {route.driver.car && (
                <p className="text-white/80 text-sm">{route.driver.car}</p>
              )}
            </div>
            {route.driver.rating !== null && (
              <div className="flex items-center gap-1 bg-white/15 px-3 py-1.5 rounded-xl">
                <Star size={14} className="text-[#FFD400] fill-[#FFD400]" />
                <span className="text-white font-bold text-sm">
                  {route.driver.rating}
                </span>
              </div>
            )}
          </div>

          {/* Route details */}
          <div className="p-5 flex flex-col gap-4">
            {/* Departure time */}
            {route.departure_time && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-[#0057FF]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">
                    Departure
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDeparture(route.departure_time)}
                  </p>
                </div>
              </div>
            )}

            {/* Recurring schedule */}
            {route.is_recurring && route.recurrence_days.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <RefreshCw size={18} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">
                    Recurring
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {route.recurrence_days
                      .map((d) => DAY_LABELS[d] ?? d)
                      .join(" · ")}
                  </p>
                </div>
              </div>
            )}

            {/* Route */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-[#FFD400]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">
                  Route
                </p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {from}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  <ArrowRight size={12} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {to}
                  </p>
                </div>
              </div>
            </div>

            {/* Seats + fare */}
            <div className="flex gap-3">
              <div
                className={`flex-1 rounded-2xl p-3.5 flex items-center gap-2.5 ${isFull ? "bg-red-50" : "bg-green-50"}`}
              >
                <Users
                  size={18}
                  className={isFull ? "text-red-500" : "text-green-600"}
                />
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                    Seats
                  </p>
                  <p
                    className={`text-sm font-bold ${isFull ? "text-red-600" : "text-green-700"}`}
                  >
                    {isFull ? "Full" : `${seatsLeft} left`}
                  </p>
                </div>
              </div>

              {route.fare_estimate !== null && (
                <div className="flex-1 rounded-2xl bg-blue-50 p-3.5 flex items-center gap-2.5">
                  <Car size={18} className="text-[#0057FF]" />
                  <div>
                    <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                      Per seat
                    </p>
                    <p className="text-sm font-bold text-[#0057FF]">
                      GH₵{route.fare_estimate}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Vibe tags */}
            {(route.preferences.ac ||
              route.preferences.quietRide ||
              route.preferences.music) && (
              <div className="flex flex-wrap gap-2">
                {route.preferences.ac && (
                  <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Snowflake size={12} /> AC
                  </span>
                )}
                {route.preferences.quietRide && (
                  <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Volume2 size={12} /> Quiet ride
                  </span>
                )}
                {route.preferences.music && (
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Music size={12} /> Music on
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        {!isFull ? (
          <button
            onClick={() => navigate(`/auth/login?next=/passenger/drivers`)}
            className="w-full bg-[#0057FF] text-white font-bold text-base py-4 rounded-2xl shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 active:opacity-90 transition-opacity"
          >
            Book this seat
            <ArrowRight size={18} />
          </button>
        ) : (
          <button
            onClick={() => navigate("/passenger/home")}
            className="w-full bg-gray-900 text-white font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 active:opacity-90 transition-opacity"
          >
            Find another driver
            <ArrowRight size={18} />
          </button>
        )}

        {/* Download nudge */}
        <p className="text-center text-xs text-gray-400 px-4 leading-relaxed">
          Don't have Yenko yet?{" "}
          <a
            href="https://yenko.vercel.app"
            className="text-[#0057FF] font-semibold underline underline-offset-2"
          >
            Download the app
          </a>{" "}
          — it's free. Skip the trotro stress and commute with people you trust.
        </p>
      </div>
    </div>
  );
}

function ErrorState({ title, body }: { title: string; body: string }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center justify-center px-6 gap-5 text-center">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
        <AlertCircle size={32} className="text-red-400" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-500 text-sm leading-relaxed max-w-xs">{body}</p>
      </div>
      <button
        onClick={() => navigate("/")}
        className="mt-2 bg-[#0057FF] text-white font-bold px-8 py-3.5 rounded-2xl"
      >
        Go to Yenko
      </button>
    </div>
  );
}
